// Display formatting. Times render in the viewer's browser time zone with a zone label.

const tzOpts = (o: Intl.DateTimeFormatOptions): Intl.DateTimeFormatOptions => o;

/** "Eastern Time" (falls back to "Eastern Daylight Time" or the IANA name). */
export function zoneName(at: Date = new Date()): string {
  for (const style of ['longGeneric', 'long'] as const) {
    try {
      const part = new Intl.DateTimeFormat('en-US', { timeZoneName: style }).formatToParts(at).find((p) => p.type === 'timeZoneName');
      if (part?.value) return part.value;
    } catch { /* style unsupported */ }
  }
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}
/** "Eastern" for running text ("at 10:30 AM Eastern"). */
export const zoneShort = (at?: Date) => zoneName(at).replace(/ Time$/, '');

const d = (iso: string | Date) => (iso instanceof Date ? iso : new Date(iso));

export const fmtTime = (iso: string | Date) =>
  new Intl.DateTimeFormat('en-US', tzOpts({ hour: 'numeric', minute: '2-digit' })).format(d(iso));
/** "Tue" */
export const fmtDow = (iso: string | Date) => new Intl.DateTimeFormat('en-US', { weekday: 'short' }).format(d(iso));
/** "Oct 27" */
export const fmtMonthDay = (iso: string | Date) => new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }).format(d(iso));
/** "Tuesday, October 27" */
export const fmtFullDay = (iso: string | Date) =>
  new Intl.DateTimeFormat('en-US', { weekday: 'long', month: 'long', day: 'numeric' }).format(d(iso));
/** "Tue, Oct 27" */
export const fmtShortDay = (iso: string | Date) => `${fmtDow(iso)}, ${fmtMonthDay(iso)}`;
/** "Oct 20, 2026" */
export const fmtDate = (iso: string | Date) =>
  new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(d(iso));
/** Calendar dates (need-by, estimates, a firm's target date) have no time: shown as written, never shifted by a time zone. */
export function fmtCalendarDate(value: string | null | undefined): string {
  if (!value) return '';
  const [y, m, day] = value.slice(0, 10).split('-').map(Number);
  return new Date(y!, m! - 1, day!).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
/** "Oct 20, 2026, 3:14 PM" */
export const fmtDateTime = (iso: string | Date) => `${fmtDate(iso)}, ${fmtTime(iso)}`;
/** Local calendar date key, "2026-10-27". */
export const localDateKey = (iso: string | Date) => {
  const x = d(iso);
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
};
/** "10:30 to 11:15 AM" (or "11:30 AM to 12:15 PM"). */
export function fmtTimeRange(start: string, end: string | null): string {
  if (!end) return fmtTime(start);
  const a = fmtTime(start), b = fmtTime(end);
  const am = (s: string) => s.slice(-2);
  return am(a) === am(b) ? `${a.slice(0, -3)} to ${b}` : `${a} to ${b}`;
}

/** "Today", "Yesterday", "3 days ago", else "Oct 12". */
export function fmtRelative(iso: string, now = new Date()): string {
  const days = Math.round((startOfDay(now) - startOfDay(d(iso))) / 86_400_000);
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  return fmtMonthDay(iso);
}
const startOfDay = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();

export const firstName = (name: string | null | undefined, fallback = 'there') => (name ?? '').trim().split(/\s+/)[0] || fallback;
export const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** "Just now", "12m ago", "3h ago", then fmtRelative ("Yesterday", "3 days ago", "Oct 12"). */
export function fmtAgo(iso: string, now = new Date()): string {
  const mins = Math.floor((now.getTime() - d(iso).getTime()) / 60_000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  if (mins < 24 * 60 && startOfDay(now) === startOfDay(d(iso))) return `${Math.floor(mins / 60)}h ago`;
  return fmtRelative(iso, now);
}

/** "Good morning" before noon, "Good afternoon" before 5 PM, else "Good evening" (viewer's clock). */
export function greeting(now = new Date()): string {
  const h = now.getHours();
  return h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening';
}

/**
 * US phone numbers as the firm types or autofills them: "7193146676" → "(719) 314-6676", built up digit by digit
 * ("(719) 3"). The result never ends in punctuation, so Backspace always removes a digit. A number with the country
 * code ("+1 719…", "1 719…") is left as typed until all 11 digits are in, then formatted without the 1 (US area
 * codes never start with 1). Anything else (an international "+44…", more than 10 digits, an extension) is left as typed.
 */
export function formatPhone(raw: string): string {
  if (/[^\d\s().+-]/.test(raw)) return raw;
  let digits = raw.replace(/\D/g, '');
  const countryCode = raw.trim().startsWith('+') || digits.startsWith('1');
  if (countryCode) {
    if (digits.length !== 11 || !digits.startsWith('1')) return raw;
    digits = digits.slice(1);
  }
  if (digits.length > 10 || digits.length === 0) return digits.length ? raw : '';
  const [a, b, c] = [digits.slice(0, 3), digits.slice(3, 6), digits.slice(6)];
  if (digits.length <= 3) return `(${a}`;
  if (digits.length <= 6) return `(${a}) ${b}`;
  return `(${a}) ${b}-${c}`;
}

/** Where the caret goes after reformatting: just past the same number of digits it was after before. */
export function caretAfterDigits(formatted: string, digitsBefore: number): number {
  if (digitsBefore <= 0) return formatted.startsWith('(') ? 1 : 0;
  let seen = 0;
  for (let i = 0; i < formatted.length; i++) if (/\d/.test(formatted[i]!) && ++seen === digitsBefore) return i + 1;
  return formatted.length;
}
